/*                                    */
import { DEV } from 'esm-env';
import { hydrating, set_hydrating } from '../hydration.js';
import { get_descriptors, get_prototype_of } from '../../../shared/utils.js';
import { create_event, delegate } from './events.js';
import { add_form_reset_listener, autofocus } from './misc.js';
import * as w from '../../warnings.js';
import { LOADING_ATTR_SYMBOL } from '#client/constants';
import { queue_idle_task } from '../task.js';
import { is_capture_event, is_delegated, normalize_attribute } from '../../../../utils.js';
import {
	active_effect,
	active_reaction,
	get,
	set_active_effect,
	set_active_reaction
} from '../../runtime.js';
import { attach } from './attachments.js';
import { clsx } from '../../../shared/attributes.js';
import { set_class } from './class.js';
import { set_style } from './style.js';
import { ATTACHMENT_KEY, NAMESPACE_HTML } from '../../../../constants.js';
import { block, branch, destroy_effect, effect } from '../../reactivity/effects.js';
import { derived } from '../../reactivity/deriveds.js';
import { init_select, select_option } from './bindings/select.js';

export const CLASS = Symbol('class');
export const STYLE = Symbol('style');

const IS_CUSTOM_ELEMENT = Symbol('is custom element');
const IS_HTML = Symbol('is html');

/**
 *
 *
 *
 *
 */
export function remove_input_defaults(input) {
	if (!hydrating) return;

	var already_removed = false;

	/*                                                                                 */
	/*                                                                                     */
	/*                                                                                  */
	/*                                                                            */
	var remove_defaults = () => {
		if (already_removed) return;
		already_removed = true;

		/*                                            */
		if (input.hasAttribute('value')) {
			var value = input.value;
			set_attribute(input, 'value', null);
			input.value = value;
		}

		if (input.hasAttribute('checked')) {
			var checked = input.checked;
			set_attribute(input, 'checked', null);
			input.checked = checked;
		}
	};

	/*               */
	input.__on_r = remove_defaults;
	queue_idle_task(remove_defaults);
	add_form_reset_listener();
}

/**
 *
 *
 */
export function set_value(element, value) {
	var attributes = get_attributes(element);

	if (
		attributes.value ===
			(attributes.value =
				/*                                                      */
				value ?? undefined) ||
		/*               */
		/*                                                            */
		(element.value === value && (value !== 0 || element.nodeName !== 'PROGRESS'))
	) {
		return;
	}

	/*               */
	element.value = value ?? '';
}

/**
 *
 *
 */
export function set_checked(element, checked) {
	var attributes = get_attributes(element);

	if (
		attributes.checked ===
		(attributes.checked =
			/*                                                      */
			checked ?? undefined)
	) {
		return;
	}

	/*               */
	element.checked = checked;
}

/**
 *
 *
 *
 *
 *
 */
export function set_selected(element, selected) {
	if (selected) {
		/*                                                           */
		/*                                                      */
		if (!element.hasAttribute('selected')) {
			element.setAttribute('selected', '');
		}
	} else {
		element.removeAttribute('selected');
	}
}

/**
 *
 *
 *
 */
export function set_default_checked(element, checked) {
	const existing_value = element.checked;
	element.defaultChecked = checked;
	element.checked = existing_value;
}

/**
 *
 *
 *
 */
export function set_default_value(element, value) {
	const existing_value = element.value;
	element.defaultValue = value;
	element.value = existing_value;
}

/**
 *
 *
 *
 *
 */
export function set_attribute(element, attribute, value, skip_warning) {
	var attributes = get_attributes(element);

	if (hydrating) {
		attributes[attribute] = element.getAttribute(attribute);

		if (
			attribute === 'src' ||
			attribute === 'srcset' ||
			(attribute === 'href' && element.nodeName === 'LINK')
		) {
			if (!skip_warning) {
				check_src_in_dev_hydration(element, attribute, value ?? '');
			}

			/*                                                                                                  */
			/*                                                                                               */
			/*                                                                                                      */
			/*                                                                           */
			return;
		}
	}

	if (attributes[attribute] === (attributes[attribute] = value)) return;

	if (attribute === 'loading') {
		/*               */
		element[LOADING_ATTR_SYMBOL] = value;
	}

	if (value == null) {
		element.removeAttribute(attribute);
	} else if (typeof value !== 'string' && get_setters(element).includes(attribute)) {
		/*         */
		element[attribute] = value;
	} else {
		element.setAttribute(attribute, value);
	}
}

/**
 *
 *
 *
 */
export function set_xlink_attribute(dom, attribute, value) {
	dom.setAttributeNS('http:/*                                        */
}

/**
 *
 *
 *
 */
export function set_custom_element_data(node, prop, value) {
	/*                                                             */
	/*                                                               */
	/*                                                                 */
	/*        */
	var previous_reaction = active_reaction;
	var previous_effect = active_effect;

	/*                                                                                   */
	/*                                                                         */
	let was_hydrating = hydrating;
	if (hydrating) {
		set_hydrating(false);
	}

	set_active_reaction(null);
	set_active_effect(null);

	try {
		if (
			/*                                                        */
			prop !== 'style' &&
			/*                                                                          */
			/*                                                                      */
			/*                                                                          */
			(setters_cache.has(node.nodeName) ||
			/*                                                                */
			!customElements ||
			customElements.get(node.tagName.toLowerCase())
				? get_setters(node).includes(prop)
				: value && typeof value === 'object')
		) {
			/*               */
			node[prop] = value;
		} else {
			/*                                                                           */
			/*                                                                           */
			/*                           */
			set_attribute(node, prop, value == null ? value : String(value));
		}
	} finally {
		set_active_reaction(previous_reaction);
		set_active_effect(previous_effect);
		if (was_hydrating) {
			set_hydrating(true);
		}
	}
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
export function set_attributes(element, prev, next, css_hash, skip_warning = false) {
	var attributes = get_attributes(element);

	var is_custom_element = attributes[IS_CUSTOM_ELEMENT];
	var preserve_attribute_case = !attributes[IS_HTML];

	/*                                                                                   */
	/*                                                                         */
	let is_hydrating_custom_element = hydrating && is_custom_element;
	if (is_hydrating_custom_element) {
		set_hydrating(false);
	}

	var current = prev || {};
	var is_option_element = element.tagName === 'OPTION';

	for (var key in prev) {
		if (!(key in next)) {
			next[key] = null;
		}
	}

	if (next.class) {
		next.class = clsx(next.class);
	} else if (css_hash || next[CLASS]) {
		next.class = null; /*                           */
	}

	if (next[STYLE]) {
		next.style ??= null; /*                           */
	}

	var setters = get_setters(element);

	/*                                 */
	for (const key in next) {
		/*                                                 */
		let value = next[key];

		/*                                                                                     */
		/*                                                                                    */
		if (is_option_element && key === 'value' && value == null) {
			/*                                                                                */
			/*                                                                                */
			/*                                                                               */
			/*                                                                                   */
			/*                                                                                */
			/*                                                                             */
			/*                                                                                   */
			/*                                                                                  */
			/*                                                                                 */
			/*                                                                                   */
			/*         */
			element.value = element.__value = '';
			current[key] = value;
			continue;
		}

		if (key === 'class') {
			var is_html = element.namespaceURI === 'http:/*                     */
			set_class(element, is_html, value, css_hash, prev?.[CLASS], next[CLASS]);
			current[key] = value;
			current[CLASS] = next[CLASS];
			continue;
		}

		if (key === 'style') {
			set_style(element, value, prev?.[STYLE], next[STYLE]);
			current[key] = value;
			current[STYLE] = next[STYLE];
			continue;
		}

		var prev_value = current[key];

		/*                                                                                          */
		if (value === prev_value && !(value === undefined && element.hasAttribute(key))) {
			continue;
		}

		current[key] = value;

		var prefix = key[0] + key[1]; /*                                  */
		if (prefix === '$$') continue;

		if (prefix === 'on') {
			/*                             */
			const opts = {};
			const event_handle_key = '$$' + key;
			let event_name = key.slice(2);
			var delegated = is_delegated(event_name);

			if (is_capture_event(event_name)) {
				event_name = event_name.slice(0, -7);
				opts.capture = true;
			}

			if (!delegated && prev_value) {
				/*                                                                                            */
				/*                                                                                                 */
				/*                                                                                          */
				/*                                              */
				if (value != null) continue;

				element.removeEventListener(event_name, current[event_handle_key], opts);
				current[event_handle_key] = null;
			}

			if (value != null) {
				if (!delegated) {
					/**
 *
 *
 */
					function handle(evt) {
						current[key].call(this, evt);
					}

					current[event_handle_key] = create_event(event_name, element, handle, opts);
				} else {
					/*         */
					element[`__${event_name}`] = value;
					delegate([event_name]);
				}
			} else if (delegated) {
				/*         */
				element[`__${event_name}`] = undefined;
			}
		} else if (key === 'style') {
			/*                     */
			set_attribute(element, key, value);
		} else if (key === 'autofocus') {
			autofocus(/*                      */ (element), Boolean(value));
		} else if (!is_custom_element && (key === '__value' || (key === 'value' && value != null))) {
			/*                                                                                */
			/*                                                                                         */
			element.value = element.__value = value;
		} else if (key === 'selected' && is_option_element) {
			set_selected(/*                            */ (element), value);
		} else {
			var name = key;
			if (!preserve_attribute_case) {
				name = normalize_attribute(name);
			}

			var is_default = name === 'defaultValue' || name === 'defaultChecked';

			if (value == null && !is_custom_element && !is_default) {
				attributes[key] = null;

				if (name === 'value' || name === 'checked') {
					/*                                                                         */
					let input = /*                           */ (element);
					const use_default = prev === undefined;
					if (name === 'value') {
						let previous = input.defaultValue;
						input.removeAttribute(name);
						input.defaultValue = previous;
						/*         */
						input.value = input.__value = use_default ? previous : null;
					} else {
						let previous = input.defaultChecked;
						input.removeAttribute(name);
						input.defaultChecked = previous;
						input.checked = use_default ? previous : false;
					}
				} else {
					element.removeAttribute(key);
				}
			} else if (
				is_default ||
				(setters.includes(name) && (is_custom_element || typeof value !== 'string'))
			) {
				/*         */
				element[name] = value;
			} else if (typeof value !== 'function') {
				set_attribute(element, name, value, skip_warning);
			}
		}
	}

	if (is_hydrating_custom_element) {
		set_hydrating(true);
	}

	return current;
}

/**
 *
 *
 *
 *
 *
 */
export function attribute_effect(
	element,
	fn,
	thunks = [],
	css_hash,
	skip_warning = false,
	d = derived
) {
	const deriveds = thunks.map(d);

	/*                                                   */
	var prev = undefined;

	/*                                 */
	var effects = {};

	var is_select = element.nodeName === 'SELECT';
	var inited = false;

	block(() => {
		var next = fn(...deriveds.map(get));
		/*                                       */
		var current = set_attributes(element, prev, next, css_hash, skip_warning);

		if (inited && is_select && 'value' in next) {
			select_option(/*                            */ (element), next.value, false);
		}

		for (let symbol of Object.getOwnPropertySymbols(effects)) {
			if (!next[symbol]) destroy_effect(effects[symbol]);
		}

		for (let symbol of Object.getOwnPropertySymbols(next)) {
			var n = next[symbol];

			if (symbol.description === ATTACHMENT_KEY && (!prev || n !== prev[symbol])) {
				if (effects[symbol]) destroy_effect(effects[symbol]);
				effects[symbol] = branch(() => attach(element, () => n));
			}

			current[symbol] = n;
		}

		prev = current;
	});

	if (is_select) {
		var select = /*                            */ (element);

		effect(() => {
			select_option(select, /*                                       */ (prev).value);
			init_select(select);
		});
	}

	inited = true;
}

/**
 *
 *
 */
function get_attributes(element) {
	return /*                                            */ (
		/*               */
		element.__attributes ??= {
			[IS_CUSTOM_ELEMENT]: element.nodeName.includes('-'),
			[IS_HTML]: element.namespaceURI === NAMESPACE_HTML
		}
	);
}

/*                                */
var setters_cache = new Map();

/*                           */
function get_setters(element) {
	var setters = setters_cache.get(element.nodeName);
	if (setters) return setters;
	setters_cache.set(element.nodeName, (setters = []));

	var descriptors;
	var proto = element; /*                                                                    */
	var element_proto = Element.prototype;

	/*                                                                                      */
	/*                                                                                */
	while (element_proto !== proto) {
		descriptors = get_descriptors(proto);

		for (var key in descriptors) {
			if (descriptors[key].set) {
				setters.push(key);
			}
		}

		proto = get_prototype_of(proto);
	}

	return setters;
}

/**
 *
 *
 *
 */
function check_src_in_dev_hydration(element, attribute, value) {
	if (!DEV) return;
	if (attribute === 'srcset' && srcset_url_equal(element, value)) return;
	if (src_url_equal(element.getAttribute(attribute) ?? '', value)) return;

	w.hydration_attribute_changed(
		attribute,
		element.outerHTML.replace(element.innerHTML, element.innerHTML && '...'),
		String(value)
	);
}

/**
 *
 *
 *
 */
function src_url_equal(element_src, url) {
	if (element_src === url) return true;
	return new URL(element_src, document.baseURI).href === new URL(url, document.baseURI).href;
}

/*                         */
function split_srcset(srcset) {
	return srcset.split(',').map((src) => src.trim().split(' ').filter(Boolean));
}

/**
 *
 *
 *
 */
function srcset_url_equal(element, srcset) {
	var element_urls = split_srcset(element.srcset);
	var urls = split_srcset(srcset);

	return (
		urls.length === element_urls.length &&
		urls.every(
			([url, width], i) =>
				width === element_urls[i][1] &&
				/*                                                                    */
				/*                                                                               */
				/*                                                                             */
				/*                                                                        */
				/*                                 */
				(src_url_equal(element_urls[i][0], url) || src_url_equal(url, element_urls[i][0]))
		)
	);
}
